import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImpactFactorTrendChart } from '@/features/journal/components/charts/ImpactFactorTrendChart';
import { JournalTopRankedChart } from '@/features/journal/components/charts/JournalTopRankedChart';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/**
 * PublicationGraphs
 * - Presentational component extracted from Publications.jsx graph section.
 * - Keeps exact markup/classes and rmVars asset paths.
 */
export default function PublicationGraphs({
  graphExpanded: externalGraphExpanded,
  setGraphExpanded: externalSetGraphExpanded,
  marginTopClass = 'mt-[18px] min-[800px]:mt-[37px]',
}) {
  const navigate = useNavigate();
  const [topRankedData, setTopRankedData] = useState(null);
  const [topRankedLoading, setTopRankedLoading] = useState(false);
  const [topRankedErr, setTopRankedErr] = useState(null);

  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsErr, setStatisticsErr] = useState(null);
  // graph toggle state - prop이 있으면 prop 사용, 없으면 내부 상태 사용
  const [internalGraphExpanded, setInternalGraphExpanded] = useState(true);
  const graphExpanded = externalGraphExpanded !== undefined ? externalGraphExpanded : internalGraphExpanded;
  const setGraphExpanded = externalSetGraphExpanded || setInternalGraphExpanded;

  // Top Ranked 데이터 가져오기
  useEffect(() => {
    let alive = true;
    async function fetchTopRanked() {
      setTopRankedLoading(true);
      setTopRankedErr(null);
      try {
        const response = await get('/rm/v1/publication/top_ranked');
        if (alive) {
          if (response?.status && response?.result) {
            setTopRankedData(response.result);
          } else {
            setTopRankedErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setTopRankedData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setTopRankedErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setTopRankedLoading(false);
        }
      }
    }
    fetchTopRanked();
    return () => { alive = false; };
  }, []);

  // 통계 데이터 가져오기
  useEffect(() => {
    let alive = true;
    async function fetchStatistics() {
      setStatisticsLoading(true);
      setStatisticsErr(null);
      try {
        const response = await get('/rm/v1/stats/general_number_stats');
        if (alive) {
          if (response?.status && response?.result) {
            const result = response.result;
            const publications = result.publications || result.publicatoins || 0;
            const citationCount = result.cited_count || 0;
            // citations_per_publication이 없으면 계산
            const citationsPerPublication = result.citations_per_publication ||
              (publications > 0 ? citationCount / publications : 0);
            // API 응답을 PublicationGraphs에서 사용하는 형식으로 변환
            const statisticsData = {
              scholarly_output: publications,
              citation_count: citationCount,
              citations_per_publication: citationsPerPublication
            };
            setStatistics(statisticsData);
          } else {
            setStatisticsErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setStatistics(null);
          }
        }
      } catch (e) {
        if (alive) {
          setStatisticsErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setStatisticsLoading(false);
        }
      }
    }
    fetchStatistics();
    return () => { alive = false; };
  }, []);

  return (
    <div className={`journal-graph ${marginTopClass} bg-white rounded-xl border border-[#E4E8F1]`}>
      <div
        className="journal-graph-toggle relative pt-[24px] pb-6 px-[20px] cursor-pointer min-[800px]:px-[25px] min-[800px]:pb-5 min-[800px]:pt-[20px]"
        onClick={() => setGraphExpanded(!graphExpanded)}
      >
        <div className="flex items-center gap-3 pr-[30px] text-[15px] font-bold leading-[130%] min-[800px]:text-[18px] min-[800px]:gap-[6px]">
          <div className="flex shrink-0 justify-center items-center w-[38px] h-[38px] rounded-lg bg-[#E4E8F1] min-[800px]:w-auto min-[800px]:h-auto min-[800px]:bg-transparent">
            <img src={`${rmVars.themeUrl}images/bar_chart_ico.png`} className="w-[22px]" alt="chart icon" />
          </div>
          Research Output Trends of Pusan National University
        </div>
        <img
          src={`${rmVars.themeUrl}images/expand_more_333.png`}
          className={`absolute right-[20px] top-[50%] w-[20px] translate-y-[-50%] transition-transform duration-300 min-[800px]:w-[24px] ${graphExpanded ? 'rotate-180' : ''}`}
          alt="toggle"
        />
      </div>

      {graphExpanded && (
        <div className="journal-graph-cont border-t border-t-[#E4E8F1] pt-5 px-[20px] min-[800px]:pt-[32px] min-[800px]:flex flex-wrap gap-[20px] min-[800px]:pb-[36px]">
          <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] min-[800px]:w-[calc((100%-20px)/2)] min-[800px]:bg-[#F3F5F9] min-[800px]:p-8 min-[800px]:mb-0">
            <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center min-[800px]:text-[16px]">
              Yearly Publications
            </h3>

            <ImpactFactorTrendChart
              height={300}
            />
          </div>

          <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] min-[800px]:bg-[#F3F5F9] min-[800px]:p-8 min-[800px]:mb-0 min-[800px]:w-[calc((100%-20px)/2)]">
            <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center">
              Number of Top-ranked Papers
            </h3>

            <div className="min-h-[240px] relative">
              {topRankedData ? (
                <JournalTopRankedChart
                  data={topRankedData}
                  onBarClick={(title) => {
                    // title에서 숫자만 추출 (예: "TOP 1%" -> "1")
                    const match = title.match(/\d+/);
                    if (match) {
                      const number = match[0];
                      const url = `/publications/?current_page=1&order_by=publish_date&order=desc&journal_ranking%5B%5D=JCR_${number}&journal_ranking%5B%5D=Scopus_${number}&journal_ranking%5B%5D=KCI_${number}`;
                      navigate(url);
                      // 스크롤을 상단으로 이동
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                      }, 0);
                    }
                  }}
                />
              ) : (
                <>
                  {/* 로딩 상태 오버레이 */}
                  <ChartSkeletonLoader show={topRankedLoading} />
                  {/* 에러 상태 오버레이 */}
                  {topRankedErr && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
                      {topRankedErr}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 mt-5 mb-5 min-[800px]:w-full min-[800px]:mt-5 min-[800px]:gap-5 min-[800px]:flex-row">
            <div className="flex items-center gap-3 p-5 bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px] min-[800px]:w-[calc((100%-40px)/3)] xl:p-[32px] xl:gap-4">
              <div className="flex items-center justify-center w-[44px] h-[44px] bg-[#E4E8F1] rounded-[8px] xl:w-[59px] xl:h-[59px]">
                <img src={`${rmVars.themeUrl}images/auto_stories_ico.png`} className="w-6" alt="icon" />
              </div>

              <div>
                <p className="text-[13px] text-[#6C7687] font-bold leading-[100%] min-[800px]:text-[16px]">Scholarly Output</p>
                <p className="mt-[6px] text-[16px] leading-[100%] font-black min-[800px]:text-[22px] min-[800px]:mt-[11px]">
                  {statisticsLoading ? '...' : (statistics?.scholarly_output ? Number(statistics.scholarly_output).toLocaleString() : '')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-5 bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px] min-[800px]:w-[calc((100%-40px)/3)] xl:p-[32px] xl:gap-4">
              <div className="flex items-center justify-center w-[44px] h-[44px] bg-[#E4E8F1] rounded-[8px] xl:w-[59px] xl:h-[59px]">
                <img src={`${rmVars.themeUrl}images/polyline_ico.png`} className="w-6" alt="icon" />
              </div>

              <div>
                <p className="text-[13px] text-[#6C7687] font-bold leading-[100%] min-[800px]:text-[16px]">Citation Count</p>
                <p className="mt-[6px] text-[16px] leading-[100%] font-black min-[800px]:text-[22px] min-[800px]:mt-[11px]">
                  {statisticsLoading ? '...' : (statistics?.citation_count ? Number(statistics.citation_count).toLocaleString() : '0')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-5 bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px] min-[800px]:w-[calc((100%-40px)/3)] xl:p-[32px] xl:gap-4">
              <div className="flex items-center justify-center w-[44px] h-[44px] bg-[#E4E8F1] rounded-[8px] xl:w-[59px] xl:h-[59px]">
                <img src={`${rmVars.themeUrl}images/percent_ico.png`} className="w-6" alt="icon" />
              </div>

              <div>
                <p className="text-[13px] text-[#6C7687] font-bold leading-[100%] min-[800px]:text-[16px]">Citations / Publication</p>
                <p className="mt-[6px] text-[16px] leading-[100%] font-black min-[800px]:text-[22px] min-[800px]:mt-[11px]">
                  {statisticsLoading ? '' : (statistics?.citation_count ? Number(statistics?.citation_count / statistics?.scholarly_output) : 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center min-[800px]:hidden pb-5">
            <button
              className="flex items-center gap-[2px] px-[10px] py-1 text-[13px] font-semibold text-[#8D9AAF] cursor-pointer"
              onClick={() => setGraphExpanded(false)}
            >
              <img src={`${rmVars.themeUrl}images/expand_more_8D9AAF.png`} className="w-[14px] rotate-180" alt="hide" />
              Hide Chart
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
